import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, CardHeader, Input, Table } from 'reactstrap';
import { OrderDropdownMenu, ServiceThreadSpinner } from '.';
import { DesktopContainer } from '../layouts';
import { api } from '../utils';

const SEND_FAILED_MESSAGE = 'The invoices could not be sent. If this persists, contact a system admin.';
// A partial or failed send lists invoice numbers to follow up, so that toast stays until it is closed.
const STICKY_TOAST = { autoClose: false };

// Order/EmailFaxInvoices answers { total, sent, failed[], notSent[], message } for a finished run; the message already
// names the invoices, so only the toast's kind is chosen here.
function toastSendOutcome({ total, sent, failed, message }) {
  if (total === 0) {
    toast.info(message);
  } else if (sent === total) {
    toast.success(message);
  } else if (failed.length === total) {
    toast.error(message, STICKY_TOAST);
  } else {
    toast.warning(message, STICKY_TOAST);
  }
}

function ResultGrid({ invoices, sending, onChangeSettings }) {
  return (
    <ServiceThreadSpinner on={sending}>
      <Table>
        <thead>
          <tr>
            <th children="Invoice #" />
            <th children="Invoice Date" />
            <th children="Customer Name" />
            <th children="Original Order #" />
            <th children="Order Date" />
            <th children="Email" />
            <th children="Fax" />
            <th children="Fax #" />
            <th children="Post Mail" />
          </tr>
        </thead>
        <tbody>
          {_.map(invoices, (i, ind) => {
            return (
              <tr key={i.invoiceNumber}>
                <OrderDropdownMenu
                  invoiceNumber={i.invoiceNumber}
                  orderId={i.orderId || i.invoiceNumber}
                  customerId={i.customerId}
                  attachmentCount={i.attachmentCount}
                  showInvoiceSettings
                  onChangeInvoiceSettings={() => onChangeSettings(i)}
                />
                <td>{i.invoiceDate}</td>
                <td>{i.customerName}</td>
                <td>{i.orderNumber}</td>
                <td>{i.orderDate === '01/01/1900' ? '' : i.orderDate}</td>
                <td>
                  <Input
                    type="checkbox"
                    checked={i.sendEmail}
                    readOnly
                    disabled
                  />
                </td>
                <td>
                  <Input
                    type="checkbox"
                    checked={i.sendFax}
                    readOnly
                    disabled
                  />
                </td>
                <td>{i.fax}</td>
                <td>
                  <Input
                    type="checkbox"
                    checked={i.sendPost}
                    readOnly
                    disabled
                  />
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    </ServiceThreadSpinner>
  )
}

export default function ProcessInvoices(props) {
  const navigate = useNavigate();
  const [emailFaxInvoices, setEmailFaxInvoices] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [postalInvoices, setPostalInvoices] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.title = `Process Invoices`;
    loadProcessInvoices();
  }, [])

  function changeSettings(i) {
    navigate('/invoice-settings', {
      state: {
        invoiceNumber: i.invoiceNumber,
        orderId: i.orderNumber || 'N/A',
        customerId: i.customerId
      }
    })
  }

  function loadProcessInvoices() {
    setLoaded(false)
    api
      .fetch('Order/LoadProcessInvoices')
      .then(r => {
        if (r.data.success) {
          setEmailFaxInvoices(r.data.emailFaxInvoices);
          setPostalInvoices(r.data.postalInvoices);
        }
      })
      .catch(api.catchHandler)
      .finally(() => setLoaded(true))
  }

  function sendEmailInvoices() {
    if (sending) return;
    setSending(true);
    api
      .fetch('Order/EmailFaxInvoices')
      .then(r => {
        if (r?.data?.success) {
          toastSendOutcome(r.data);
        } else if (!api.isHandledByInterceptor(r)) {
          toast.error(r?.data?.message || SEND_FAILED_MESSAGE, STICKY_TOAST);
        }
      })
      .catch(api.catchHandler)
      .finally(() => {
        setSending(false);
        loadProcessInvoices();
      });
  }

  return (
    <DesktopContainer screenName="Process Invoices">
      <ServiceThreadSpinner on={!loaded}>
        <Card>
          <CardHeader className='filter-header'>
            <div style={{paddingLeft: "1vw", color: "white"}}>
              <span className="h2">Posted Invoices to Be Sent via Email/Fax</span>
            </div>
          </CardHeader>
          <CardBody className='pt-0'>
            <hr />
            {emailFaxInvoices.length > 0
              ? (
                <>
                  <ResultGrid invoices={emailFaxInvoices} sending={sending} onChangeSettings={changeSettings} />
                  <Button
                    className='shredButton'
                    onClick={sendEmailInvoices}
                    disabled={sending}
                  >Send</Button>
                </>
              )
              : <span className="h5">No Email/Fax invoices found</span>}
          </CardBody>
        </Card>
        <br />
        <Card>
        <CardHeader className='filter-header'>
            <div style={{paddingLeft: "1vw", color: "white"}}>
              <span className="h2">Posted Invoices to Be Sent via Postal Mail</span>
            </div>
          </CardHeader>
          <CardBody className='pt-0'>
            <hr />
            {postalInvoices.length > 0
              ? <ResultGrid invoices={postalInvoices} onChangeSettings={changeSettings} />
              : <span className="h5">No Postal Mail invoices found</span>}
          </CardBody>
        </Card>

      </ServiceThreadSpinner>
    </DesktopContainer>
  );
}
